import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Switch,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  useTheme,
  SPACING,
  BORDER_RADIUS,
  CONTENT_MAX_WIDTH,
  FONT_SIZES,
  TYPOGRAPHY,
  withWeight,
} from '../../src/theme';
import Head from 'expo-router/head';
import { Header } from '@components/layout';
import { switchColors } from '../../src/utils/webStyles';
import { useAuthStore } from '../../src/store/authStore';
import { useNotificationsStore } from '../../src/store/notificationsStore';
import { NotificationPreferences } from '../../src/types';

interface NotificationSetting {
  id: keyof NotificationPreferences;
  label: string;
  description: string;
}

const PUSH_SETTINGS: NotificationSetting[] = [
  {
    id: 'newPosts',
    label: 'New Posts',
    description: 'When someone posts about a property you follow',
  },
  {
    id: 'comments',
    label: 'Comments',
    description: 'When someone comments on your posts',
  },
  {
    id: 'likes',
    label: 'Likes',
    description: 'When someone likes your posts',
  },
  {
    id: 'checklistReminders',
    label: 'Checklist Reminders',
    description: 'Reminders to complete your insurance checklist',
  },
  {
    id: 'communityUpdates',
    label: 'Community Updates',
    description: 'News and updates from your community',
  },
];

const DEFAULT_PREFERENCES: NotificationPreferences = {
  newPosts: true,
  comments: true,
  likes: true,
  checklistReminders: true,
  communityUpdates: true,
};

export default function NotificationsScreen() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { user, updateProfile } = useAuthStore();
  const registerWebPushToken = useNotificationsStore((s) => s.registerWebPushToken);

  const [emailEnabled, setEmailEnabled] = useState(user?.emailNotificationsEnabled ?? true);

  // Derive current preferences from the user doc, defaulting all to true when
  // the field doesn't exist yet (new users).
  const prefs: NotificationPreferences = user?.notificationPreferences ?? DEFAULT_PREFERENCES;

  const handleEmailToggle = async (value: boolean) => {
    setEmailEnabled(value);
    try {
      await updateProfile({ emailNotificationsEnabled: value });
    } catch {
      setEmailEnabled(!value);
    }
  };

  const handlePushToggle = async (id: keyof NotificationPreferences, newValue: boolean) => {
    const updated: NotificationPreferences = { ...prefs, [id]: newValue };
    try {
      await updateProfile({ notificationPreferences: updated });
    } catch {
      // updateProfile throws — the store will not have updated, so no local
      // rollback needed; the switch will re-render from the unchanged store value.
    }
  };

  // Web-only: Notification permission status (tracked in state so UI updates after grant)
  const isWeb = Platform.OS === 'web';
  const [webPermission, setWebPermission] = useState<NotificationPermission | null>(
    isWeb && typeof Notification !== 'undefined' ? Notification.permission : null
  );

  const handleEnablePush = async () => {
    // Web-only flow (Notification API + service worker). The button only
    // renders on web, but guard explicitly so this can never run on native.
    if (!isWeb || !user) return;
    try {
      const permission = await registerWebPushToken(user.id);
      setWebPermission(permission);
    } catch (err) {
      console.warn('Push notification setup failed:', err);
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Head>
        <title>Notifications - Redi</title>
      </Head>
      <Header showBack showAvatar={false} />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.content, { paddingBottom: insets.bottom + SPACING.xl }]}>
          <Text style={[styles.screenTitle, { color: colors.text }]}>Notifications</Text>
          {/* Email section */}
          <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>Email</Text>
          <View style={[styles.section, { borderTopColor: colors.border }]}>
            <View style={[styles.item, { borderBottomColor: colors.border }]}>
              <View style={styles.itemContent}>
                <Text style={[styles.itemLabel, { color: colors.text }]}>Email Notifications</Text>
                <Text style={[styles.itemDescription, { color: colors.textSecondary }]}>
                  Receive updates and alerts via email
                </Text>
              </View>
              <Switch
                value={emailEnabled}
                onValueChange={handleEmailToggle}
                // The visible row text is a sibling, not a <label>, so RN Web
                // renders an unnamed checkbox. Name every Switch explicitly.
                accessibilityLabel="Email Notifications"
                {...switchColors(colors)}
              />
            </View>
          </View>

          {/* Push notifications section */}
          <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>
            Push Notifications
          </Text>

          {/* Permission status row (web only) */}
          {isWeb && webPermission !== null && (
            <View
              style={[styles.section, { borderTopColor: colors.border }, styles.permissionSection]}>
              <View style={styles.permissionRow}>
                <View style={styles.itemContent}>
                  <Text style={[styles.itemLabel, { color: colors.text }]}>Permission Status</Text>
                  <Text style={[styles.itemDescription, { color: colors.textSecondary }]}>
                    {webPermission === 'granted'
                      ? 'Push notifications are enabled'
                      : webPermission === 'denied'
                        ? 'Push notifications are blocked in your browser'
                        : 'Push notifications have not been enabled yet'}
                  </Text>
                </View>
                {webPermission !== 'granted' && webPermission !== 'denied' && (
                  <TouchableOpacity
                    onPress={handleEnablePush}
                    style={[styles.permissionButton, { backgroundColor: colors.action }]}>
                    <Text style={[styles.permissionButtonText, { color: colors.actionOn }]}>
                      Enable
                    </Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
          )}

          <View style={[styles.section, { borderTopColor: colors.border }]}>
            {PUSH_SETTINGS.map((setting) => (
              <View key={setting.id} style={[styles.item, { borderBottomColor: colors.border }]}>
                <View style={styles.itemContent}>
                  <Text style={[styles.itemLabel, { color: colors.text }]}>{setting.label}</Text>
                  <Text style={[styles.itemDescription, { color: colors.textSecondary }]}>
                    {setting.description}
                  </Text>
                </View>
                <Switch
                  value={prefs[setting.id]}
                  onValueChange={(newValue) => handlePushToggle(setting.id, newValue)}
                  accessibilityLabel={setting.label}
                  {...switchColors(colors)}
                />
              </View>
            ))}
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  scrollView: { flex: 1 },
  scrollContent: { alignItems: 'center' },
  content: {
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.sm,
  },
  screenTitle: {
    ...TYPOGRAPHY.screenTitle,
    marginBottom: SPACING.sm,
  },
  sectionTitle: {
    ...TYPOGRAPHY.overline,
    marginBottom: SPACING.sm,
    marginTop: SPACING.md,
  },
  // Hairline rows, not filled panels (mockup `.rowlink`).
  section: {
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  permissionSection: {
    marginBottom: SPACING.sm,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SPACING.smd,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  itemContent: {
    flex: 1,
    marginRight: SPACING.md,
  },
  itemLabel: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
    fontSize: FONT_SIZES.md,
  },
  itemDescription: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 2,
  },
  permissionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SPACING.smd,
  },
  permissionButton: {
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    borderRadius: BORDER_RADIUS.full,
  },
  permissionButtonText: {
    ...TYPOGRAPHY.button,
  },
});
